import { InvoicePaymentStatus } from '../../models/invoice-payment-status';
import { EditProps, Invoice, InvoiceData } from "../../models/table-models";
import { Pagination } from "../../utils/pagination-hooks";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { DocumentView } from "../../models/dashboard-models";
import { getCreditNotePDF, getInvoicePDF, saveInvoicesToDocument, useGetInvoices } from "../../utils/clients-hooks";
import { resourceCache } from "use-async-resource";
import { useGetAllTurnovers } from "../../utils/users-hooks";
import { toast } from "react-toastify";
import { CustomToastContent } from "../CustomToastContent";
import { toastOptionsTopRight } from "../../utils/Enums/ToastOptions";
import { PageSizeComponent } from "../PageSizeComponent";
import { InvoiceView } from "../../pages/InvoiceView";
import { CustomButton } from "../CustomButton";
import { CustomTable } from "../CustomTable";
import { invoicesTableHeaders } from "../../utils/Enums/TableHeaders";
import { transformDateToString, transformPriceToString, getCurrencySymbol } from "../../utils/helper-functions";
import { Role } from "../../utils/Enums/Roles";
import { AddInvoicesToDocument, EditDocument } from "../AddInvoicesToDocument";
import { PaginationComponent } from "../Pagination";
import { Button, Modal } from "react-bootstrap";
import { NoDataComponent } from "../NoDataComponent";

const emptyInvoices: Invoice[] = [];

export const ClientInvoicesInner = ({ invoicesData, refetchEntitiesHook, pagination }: {
    invoicesData?: InvoiceData<Invoice[]>,
    refetchEntitiesHook: () => void,
    pagination: Pagination
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });

    const invoices = invoicesData?.paidInvoices ?? invoicesData?.unpaidInvoices ?? emptyInvoices;
    const pager = invoicesData?.pager;

    const [selectedItems, setSelectedItems] = useState<Invoice[]>([]);
    const { loggedUser } = useContext(AuthContext);

    const [comment, setComment] = useState("");
    const [selectedItem, setSelectedItem] = useState<Invoice | undefined>(undefined);
    const nonSelectableItems = useMemo(() => invoices.filter(({ document }) => document?.invoiceNumber != null), [invoices]);
    const [showModal, setShowModal] = useState(false);
    const [oneDocument, setOneDocument] = useState(false);
    const [document, setDocument] = useState<DocumentView | undefined>(undefined);

    useEffect(() => {
        const selectedIds = selectedItems.map((value) => value.id as number);

        const filteredInvoices = invoices.filter((value) => selectedIds.includes(value.id as number));

        setSelectedItems(current => current.length === filteredInvoices.length &&
            current.every((invoice, index) => invoice === filteredInvoices[index]) ? current : filteredInvoices);

        const differentDocument = filteredInvoices.some((item) => item.document?.id !== filteredInvoices[0].document?.id);
        if (!differentDocument) {
            setDocument(() => {
                if (!selectedItems[0]?.document) {
                    return undefined;
                }
                return {
                    ...selectedItems[0].document,
                    invoices: selectedIds
                } as DocumentView;
            });
        }
        setOneDocument(() => filteredInvoices.length > 0 && !differentDocument);
        if (selectedItem != undefined) {
            setSelectedItem(() => filteredInvoices[0])
        }
    }, [invoices]);

    const save = async (document?: DocumentView) => {
        await saveInvoicesToDocument(document);

        if (document?.isPaid === InvoicePaymentStatus.Paid) {
            setShowModal(() => false);
            resourceCache(useGetAllTurnovers).clear();
        }

        refetchEntitiesHook();

        toast.success(<CustomToastContent content={[t('DocumentComfirm')]} />, toastOptionsTopRight);
    };

    const print = async () => {
        const pdfFile = await getInvoicePDF(selectedItems[1]?.document?.id, loggedUser?.id);
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            return true;
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
            return false;
        }
    };

    const handleCreditNoteSubmit = async () => {
        const pdfFile = await getCreditNotePDF(null, document?.id, loggedUser?.id);
        resourceCache(useGetInvoices).clear();
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            return true;
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
            return false;
        }
    };

    useEffect(() => {
        if (selectedItems.length > 0) {
            const selectedIds = selectedItems.map((value) => value.id as number);

            const differentDocument = selectedItems.some((item) => item.document?.id !== selectedItems[0].document?.id);
            if (!differentDocument) {
                setDocument(() => {
                    if (!selectedItems[0]?.document) {
                        return undefined;
                    }
                    return {
                        ...selectedItems[0].document,
                        invoices: selectedIds
                    } as DocumentView;
                });
            }
            setOneDocument(() => !differentDocument);
        } else
            setOneDocument(() => false);
    }, [selectedItems]);

    return (
        <>
            <PageSizeComponent {...{ pagination }} itemsToShow={[5, 10, 20, 50, 75, 100]} />
            {selectedItem &&
                <InvoiceView title={t('Details for') + " " + t(selectedItem?.documentType + "")} item={selectedItem}
                    setSelectedItem={setSelectedItem} refetchEntitiesHook={() => {
                        resourceCache(useGetInvoices).clear();
                        refetchEntitiesHook()
                    }
                    } />}
            {invoices.length > 0 ? (
                <>
                    {selectedItems.length > 0 &&
                        <CustomButton
                            className='w-30 mb-3'
                            onClick={() => {
                                setShowModal(true)
                            }}
                            title={t('generateDocumentForSelected')}
                        />
                    }
                    <CustomTable
                        selectable
                        columns={{
                            headings: invoicesTableHeaders.filter(value => value != 'paymentProcessingStatus' ),
                            items: invoices.map(
                                ({
                                    branchName,
                                    companyName,
                                    currency,
                                    eik,
                                    fromDate,
                                    duePaymentDate,
                                    creationDate,
                                    documentType,
                                    user,
                                    paymentMethod,
                                    total,
                                    document,
                                }) => [
                                        document?.invoiceNumber,
                                        transformDateToString(creationDate),
                                        t(documentType),
                                        branchName,
                                        document?.clientName ?? companyName,
                                        document?.eik ?? eik,
                                        transformDateToString(fromDate),
                                        transformDateToString(duePaymentDate),
                                        user,
                                        `${transformPriceToString(total)}${getCurrencySymbol(currency)}`,
                                        t(paymentMethod),
                                    ],
                            ),
                        }}
                        viewComponentObject={(invoice) => {
                            const item = invoices.find((value) => value.id === invoice.id && value.documentType === invoice.documentType);
                            setSelectedItem(item);
                        }}
                        accessEditRoles={[Role.FINANCIAL, Role.COORDINATOR]}
                        accessActionRoles={[Role.FINANCIAL, Role.COORDINATOR]}
                        comment={comment}
                        setComment={setComment}
                        editComponent={
                            ({ itemToEdit, setShowModal }: EditProps<Invoice>) =>
                                <EditDocument itemToEdit={itemToEdit} setShowModal={setShowModal} invoices={invoices}
                                    refetchEntitiesHook={refetchEntitiesHook} />
                        }
                        tableData={invoices}
                        setSelectedItems={setSelectedItems}
                        notSelectable={nonSelectableItems}
                        refetchEntitiesHook={refetchEntitiesHook}
                    />
                    <PaginationComponent recordsCount={pager?.totalCount ?? 0} pagination={pagination} />
                    <Modal
                        show={showModal}
                        onHide={() => setShowModal(false)}
                        size='xl'
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title id='contained-modal-title-vcenter'>
                                <label>
                                    {t("createDocument")}
                                </label>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className='d-flex w-100 justify-content-center p-0 m-0'>
                                <AddInvoicesToDocument setShowModal={setShowModal}
                                    invoices={selectedItems}
                                    document={document}
                                    refetchEntitiesHook={refetchEntitiesHook}
                                    onSubmit={save}
                                    setDocument={setDocument}
                                />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            {selectedItems[0]?.document?.invoiceNumber && <Button form='form' type='submit'
                                onClick={() => handleCreditNoteSubmit()}>{t('printCreditNote')}</Button>}
                            <Button form='form' type='submit' className={"btn-success"}>{t('save')}</Button>
                            {oneDocument && <Button
                                onClick={() => selectedItems[0].document?.creditNoteNumber ? handleCreditNoteSubmit() : print()}>{t('print')}</Button>}
                            <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )
                :
                (
                    <NoDataComponent noDataText={t('invoices')}></NoDataComponent>
                )
            }
        </>
    );
};
